// variable-selection.component.ts
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { VariableService } from '../../../../shared/ontology/service/variable.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { VariableDetails } from '../../../../shared/ontology/model/variable-details';
import { VariableTypeEnum } from '../../../../shared/ontology/variable-type.enum';
import { VariableFilterRequest } from '../../../../shared/ontology/model/variable-filter-request';
import { finalize } from 'rxjs/operators';
import { AlertService } from '../../../../shared/alert/alert.service';
import { VariableUtilityService } from '../../services/variable-utility.service';
import { ALLOWED_ADDITIONAL_SYSTEM_VARIABLES } from '../../../../app.constants';
import { SubscriptionsManager } from '../../../../shared/util/subscriptions-manager';

@Component({
    selector: 'jhi-study-variable-select-modal',
    templateUrl: './study-variable-select-modal.component.html',
    styleUrls: ['./study-variable-select-modal.component.scss',]

})
export class StudyVariableSelectModalComponent implements OnInit, OnDestroy {

    // List of variables to hide from the variable selection
    HIDE_FROM_SELECTION = [8372, 8110, 8190, 8070, 8040, 8170, 8256, 8411, 8412, 8413, 8257, 8190, 8180, 8189];
    ALLOWED_ADDITIONAL_SYSTEM_VARIABLES = ALLOWED_ADDITIONAL_SYSTEM_VARIABLES;

    @Output() variableSelect = new EventEmitter<VariableDetails>();
    @Output() modalHidden = new EventEmitter<void>();

    // Filters
    @Input() variableTypeIds: VariableTypeEnum[] = [];
    @Input() datasetId: number;
    @Input() germplasmUUID: string;
    @Input() excludedVariableIds: number[] = [];
    @Input() existingVariableIdsInStudy: number[] = [];
    @Input() excludedVariableNames: string[] = [];
    @Input() disableVariablesFromSelector: number[] = [];
    @Input() lotId: number;
    @Input() projectId: number;
    @Input() selectionType: 'single' | 'multiple' = 'multiple';

    @Input() disabled = false;
    @Input() preventAddSystemVariables = false;

    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    addedVariableIds: string[] = []; // To keep track of added variables
    variables: VariableDetails[] = [];
    variableById: { [key: string]: VariableDetails } = {};
    groupedVariables: { [key: string]: VariableDetails[] } = {};
    searchText: string = '';
    filteredGroupedVariables: { [key: string]: VariableDetails[] } = {};
    isLoading = true;

    selectedProperty: {
        id: number
        name: string,
        classes: any
    } = null;
    availableProperties: {
        id: number
        name: string,
        classes: any
    }[] = [];
    relatedProperties: {
        id: number
        name: string,
        classes: any
    }[];

    constructor(private variableService: VariableService,
                private activeModal: NgbActiveModal,
                private alertService: AlertService,
                private variableUtilityService: VariableUtilityService) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {

        this.disableVariablesFromSelector = this.disableVariablesFromSelector || [];
        this.existingVariableIdsInStudy = this.existingVariableIdsInStudy || [];

        const filterRequest = <VariableFilterRequest>({});
        if (this.datasetId) {
            filterRequest.datasetIds = [this.datasetId];
        }
        if (this.germplasmUUID) {
            filterRequest.germplasmUUIDs = [this.germplasmUUID];
        }
        if (this.variableTypeIds && this.variableTypeIds.length) {
            filterRequest.variableTypeIds = this.variableTypeIds.map((enumType) => enumType.toString());
        }
        if (this.projectId) {
            filterRequest.projectId = this.projectId;
        }
        if (this.lotId) {
            filterRequest.lotIds = [this.lotId];
        }
        if (this.disabled) {
            // show obsoletes when not editing
            filterRequest.showObsoletes = true;
        }
        this.subscriptionsManager.add = this.variableService.filterVariables(filterRequest).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe((variables) => {
            this.variables = this.transform(variables);
            this.populateProperties(this.variables);
            this.filterVariables();
        });

        // Append the HIDE_FROM_SELECTION to the list of variables to exclude
        this.excludedVariableIds.push(...this.HIDE_FROM_SELECTION);
    }

    onPropertySelect($event: any) {

    }

    hide() {
        this.addedVariableIds = []; // Reset added variable tracking
        this.activeModal.close();
    }

    transform(variables: VariableDetails[]): any[] {
        const filteredVariables = variables.filter((variable) => (
            (!this.excludedVariableIds || !this.excludedVariableIds.includes(parseInt(variable.id, 10)))
            && (!this.excludedVariableNames || !this.excludedVariableNames.includes(variable.name.toUpperCase()))));

        this.variableById = variables.map(variable => ({
            [variable.id]: variable
        })).reduce((acc, cur) => ({
            ...acc,
            ...cur
        }), {});

        // Group variables by property.id
        this.groupedVariables = filteredVariables.reduce((groups, variable) => {
            const propertyId = variable.property?.id || 'undefined';
            if (!groups[propertyId]) {
                groups[propertyId] = [];
            }
            groups[propertyId].push(variable);
            return groups;
        }, {} as { [key: string]: VariableDetails[] });

        return filteredVariables;
    }

    filterVariables() {
        let filtered = { ...this.groupedVariables };

        // Filter by selected property
        if (this.selectedProperty) {
            filtered = Object.keys(filtered).reduce((acc, key) => {
                if (key === this.selectedProperty.id.toString()) {
                    acc[key] = filtered[key];
                }
                return acc;
            }, {} as { [key: string]: VariableDetails[] });
        }

        // Then apply text search if exists
        if (this.searchText) {
            const searchTerm = this.searchText.toLowerCase();
            filtered = Object.keys(filtered).reduce((acc, key) => {
                const filteredByText = filtered[key].filter(variable =>
                    variable.name?.toLowerCase().includes(searchTerm) ||
                    variable.description?.toLowerCase().includes(searchTerm) ||
                    variable.property?.name?.toLowerCase().includes(searchTerm) ||
                    variable.property?.classes?.toString().toLowerCase().includes(searchTerm)
                );

                if (filteredByText.length > 0) {
                    acc[key] = filteredByText;
                }
                return acc;
            }, {} as { [key: string]: VariableDetails[] });
        }

        // Exclude added variables
        Object.keys(filtered).forEach((key) => {
            filtered[key] = filtered[key].filter((variable) => !this.addedVariableIds.includes(variable.id));
            if (filtered[key].length === 0) {
                delete filtered[key]; // Remove empty groups
            }
        });


        this.filteredGroupedVariables = filtered;
    }


    highlightText(text: string): string {
        if (!text && !this.searchText) {
            return text;
        }
        const searchTerm = this.searchText.toLowerCase();
        const index = text.toLowerCase().indexOf(searchTerm);
        if (index === -1) {
            return text;
        }
        return text.slice(0, index) +
            `<span class="text-highlight">${text.slice(index, index + this.searchText.length)}</span>` +
            text.slice(index + this.searchText.length);
    }

    // Method to populate available properties
    private populateProperties(variables: VariableDetails[]) {
        // Extract unique properties from variables
        this.availableProperties = Array.from(
            new Set(variables
                .filter(v => v.property)
                .map(v => JSON.stringify({
                    id: v.property.id,
                    name: v.property.name,
                    classes: v.property.classes
                }))
            )
        ).map(str => JSON.parse(str));
    }

    onPropertyChange() {
        this.filterVariables();
        this.relatedProperties = [];

        // Filter by selected property class if property is selected
        if (this.selectedProperty) {
            const selectedPropertyClass = this.selectedProperty.classes;
            this.relatedProperties = this.availableProperties.filter(p =>
                p.classes.some(c => selectedPropertyClass.includes(c))
            );
        }
    }

    onAddVariable(variable: VariableDetails) {

        if (this.selectionType === 'single') {
            this.addedVariableIds = [variable.id];
            this.variableSelect.emit(variable);
            this.activeModal.dismiss();
            return;
        }

        const variableId = parseInt(variable.id, 10);
        // // Remove the added variable from the group
        // const propertyId = variable.property?.id || 'undefined';
        //
        // if (this.filteredGroupedVariables[propertyId]) {
        //     const updatedGroup = this.filteredGroupedVariables[propertyId].filter((v) => v.id !== variable.id);
        //
        //     if (updatedGroup.length > 0) {
        //         // Update the group with remaining variables
        //         this.filteredGroupedVariables = {
        //             ...this.filteredGroupedVariables,
        //             [propertyId]: updatedGroup
        //         };
        //     } else {
        //         // Remove the group entirely if it's empty
        //         const { [propertyId]: _, ...remainingGroups } = this.filteredGroupedVariables;
        //         this.filteredGroupedVariables = remainingGroups;
        //     }
        // }

        if (this.validateSystemVariable(variable)) {
            // Add the variable's ID to the addedVariableIds array
            this.addedVariableIds.push(variableId.toString());
            this.existingVariableIdsInStudy.push(variableId);
            const translatedVariableId = this.variableUtilityService.getIDPairByVariableId(variableId);
            if (translatedVariableId !== variableId) {
                // If the added variable has ID-NAME variable pair,
                // Emit the ID variable instead of NAME
                const idVariable = this.variableById[translatedVariableId.toString()];
                if (idVariable) {
                    this.addedVariableIds.push(translatedVariableId.toString());
                    idVariable.alias = variable.alias;
                    idVariable.name = variable.name;
                    this.variableSelect.emit(idVariable);
                }
            } else {
                this.variableSelect.emit(variable);
            }
        } else {
            this.alertService.error('study-editor.variable.table.error.system-variable-cannot-be-added');
        }
    }

    validateSystemVariable(variable: VariableDetails): boolean {
        const variableTypeIds: number[] = variable.variableTypes.map(vt => parseInt(vt.id, 10));
        if (this.preventAddSystemVariables && variable.metadata?.usage?.systemTerm) {
            return variableTypeIds.includes(VariableTypeEnum.EXPERIMENTAL_DESIGN) && this.ALLOWED_ADDITIONAL_SYSTEM_VARIABLES.includes(parseInt(variable.id, 10));
        }
        return true;
    }

    onRelatedPropertyClick(property: { id: number, name: string, classes: any }) {
        this.selectedProperty = property;
        this.onPropertyChange();
    }

    // Optimization
    trackByProperty(index: number, propertyGroup: any): string {
        return propertyGroup.key;
    }

    trackByVariable(index: number, variable: any): number | string {
        return variable.id || index;
    }

    trackByFormula(index: number, input: any): number | string {
        return input.id || input.name;
    }

    propertySelectSearchFunction = (term: string, item: any): boolean => {
        const termLower = term.toLowerCase();

        // Search in property name
        const nameMatch = item.name.toLowerCase().includes(termLower);

        // Search in classes
        const classesMatch = Array.isArray(item.classes)
            ? item.classes.some((c: string) => c.toLowerCase().includes(termLower))
            : String(item.classes).toLowerCase().includes(termLower);

        return nameMatch || classesMatch;
    };

    getFormula(variable: VariableDetails) {
        // Create a map for O(1) lookup
        const idToNameMap = new Map(
            variable.formula.inputs.map(input => [input.id.toString(), input.name])
        );

        // Replace all {{id}} patterns with corresponding names wrapped in badge
        const formulaWithBadges = variable.formula.definition.replace(/\{\{(\d+)\}\}/g, (match, id) => {
            const name = idToNameMap.get(id);
            return name ? `<span class="badge badge-pill text-bg-light border">${name}</span>` : match;
        });

        // Wrap the entire formula in Bootstrap 5.3 code styling
        return `<code class="text-body fs-6">${formulaWithBadges}</code>`;

    }

    protected readonly Number = Number;
}
